import type React from 'react';
import { useCallback, useRef } from 'react';

import { MAX_FILESTACK_FILE_SIZE, mentionType } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import {
  aiChatAcceptedMimeTypes,
  extractFileName,
  useFilestackClient,
  useGetFreshFilestackClient,
} from 'src/helpers/integrations/filestack';
import { multiuploadWithAuthRetry } from 'src/helpers/integrations/filestack-multiupload';
import { parseFilestackUploadPayloads } from 'src/helpers/integrations/filestack-upload-payload';
import { useCreateFiles, useNotificationDispatch } from 'src/hooks';
import { useFileDragState } from 'src/hooks/drag-and-drop';
import type { FileDragProps } from 'src/hooks/drag-and-drop';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import type { AlineMarkdownEditorRef, DocumentMentionElement } from '../ChatPanel/ChatEditor';
import { insertMentionAt, MAX_CHAT_UPLOAD_FILES } from '../ChatPanel/ChatEditor';

interface UseChatEditorDropzoneOptions {
  editorRef: React.RefObject<AlineMarkdownEditorRef | null>;
  disabled?: boolean;
  maxFiles?: number;
}

interface UseChatEditorDropzoneReturn {
  dropzoneProps: FileDragProps & {
    onPaste: (e: React.ClipboardEvent) => void;
  };
  /** react-dropzone's root ref; already included in dropzoneProps. Exposed so consumers can read the dropzone element (e.g. as a popover anchor). */
  dropzoneRootRef: React.RefObject<HTMLElement>;
  isDragging: boolean;
}

export function useChatEditorDropzone(
  options: UseChatEditorDropzoneOptions
): UseChatEditorDropzoneReturn {
  const dispatch = useNotificationDispatch();
  const isUploadingRef = useRef(false);
  const { createFiles } = useCreateFiles();
  const filestackClient = useFilestackClient();
  const getFreshFilestackClient = useGetFreshFilestackClient();
  const { disabled = false, editorRef, maxFiles = MAX_CHAT_UPLOAD_FILES } = options;

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (disabled || isUploadingRef.current) return;

      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      const validFiles = files.filter(file => {
        return file.size <= MAX_FILESTACK_FILE_SIZE && isFileAccepted(file);
      });
      const invalidFileCount = files.length - validFiles.length;
      if (invalidFileCount > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: `Only Word and PDF files are supported. Skipping ${invalidFileCount} file${invalidFileCount > 1 ? 's' : ''}.`,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      const filesToUpload = validFiles.slice(0, maxFiles);
      if (!filesToUpload.length) return;

      const filesOverMaxCount = validFiles.length - maxFiles;
      if (filesOverMaxCount > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: `You can only upload up to ${maxFiles} files at a time. Skipping ${filesOverMaxCount} file${filesOverMaxCount > 1 ? 's' : ''}.`,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      isUploadingRef.current = true;
      const fileMetadata = filesToUpload.map(file => {
        const tempId = uuid4();
        const filename = extractFileName(file.name);

        // Insert placeholder mention immediately for good UX.
        insertMentionAt(editor, {
          mentionNode: {
            children: [{ text: '' }],
            documentId: tempId,
            documentName: filename,
            isPlaceholder: true,
            mentionTypeName: 'document',
            type: mentionType,
            value: filename,
          } satisfies DocumentMentionElement,
        });

        return { file, filename, tempId };
      });

      try {
        const uploadResults = await multiuploadWithAuthRetry({
          files: fileMetadata.map(m => m.file),
          filestackClient,
          getFreshFilestackClient,
        });
        const uploadPayloads = parseFilestackUploadPayloads(uploadResults, fileMetadata.length);
        const { data } = await createFiles({
          variables: {
            input: {
              records: uploadPayloads.map((result, i) => ({
                documentProps: {
                  isSigned: false,
                  variant: Apollo.DocumentVariant.Document,
                },
                fileLocationProps: {
                  container: result.container,
                  filename: fileMetadata[i].filename,
                  handle: result.handle,
                  key: result.key,
                  mimetype: result.mimetype,
                  size: result.size,
                  url: result.url,
                },
              })),
            },
          },
        });
        const createdFiles = data?.createFiles ?? [];

        // Update placeholders with real IDs.
        createdFiles.forEach((createdFile, i) => {
          const { tempId } = fileMetadata[i];
          const entry = editor.api
            .nodes({
              at: [],
              match: n => n.type === mentionType && n.documentId === tempId,
            })
            .next().value;
          if (entry) {
            const [, path] = entry;
            editor.tf.setNodes(
              {
                documentId: createdFile.id,
                documentName: createdFile.name,
                isPlaceholder: false,
                value: createdFile.name,
              } satisfies Pick<
                DocumentMentionElement,
                'documentId' | 'documentName' | 'isPlaceholder' | 'value'
              >,
              { at: path, voids: true }
            );
          }
        });
      } catch (error) {
        dispatch({
          payload: {
            key: uuid4(),
            message: error instanceof Error ? error.message : 'Upload failed.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      } finally {
        isUploadingRef.current = false;
      }
    },
    [disabled, maxFiles, filestackClient, getFreshFilestackClient, createFiles, dispatch, editorRef]
  );

  const handleDropFiles = useCallback(
    (files: File[]) => {
      void uploadFiles(files);
    },
    [uploadFiles]
  );

  const { dragProps, isDragging, rootRef } = useFileDragState({
    disabled,
    onDropFiles: handleDropFiles,
  });

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const pastedFiles = Array.from(e.clipboardData?.files ?? []);
      if (pastedFiles.length) {
        e.preventDefault();
        void uploadFiles(pastedFiles);
      }
      // If no files, let the default paste behavior handle text...
    },
    [uploadFiles]
  );

  return {
    dropzoneProps: { ...dragProps, onPaste: handlePaste },
    dropzoneRootRef: rootRef,
    isDragging,
  };
}

function isFileAccepted(file: File): boolean {
  return aiChatAcceptedMimeTypes.has(file.type);
}
